"use client";
import { TODOS } from "@/lib/idiomas";
import React, { useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import Cookies from "js-cookie";
import { usePaisDetectado } from "@/lib/geo/pais";
import api from "@/lib/api";
import { bookGallery } from "@/lib/books/gallery";
import { usePathname } from "next/navigation";
import { useSearchParamsDelVisitante } from "@/lib/useSearchParamsDelVisitante";
import { useTranslation } from "react-i18next";
import { trackViewItemList, trackLead } from "@/lib/analytics";
import booksEs from "@/locales/es/books.json";

const getEs = (key) => {
  const parts = key.split(".");
  let val = booksEs;
  for (const p of parts) {
    if (val == null) return key;
    val = val[p];
  }
  return typeof val === "string" ? val : key;
};

const normalizeCC = (value) => {
  const cc = String(value || "")
    .trim()
    .slice(0, 2)
    .toUpperCase();
  return /^[A-Z]{2}$/.test(cc) ? cc : "";
};

const EUROPE_ISO_SET = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO", "CH", "GB", "AL", "RS", "UA", "MD",
  "BY", "GE", "AM", "AZ", "AD", "MC", "SM", "VA", "ME", "BA", "MK", "XK",
]);

const PRODUCT_IDS = { ARMONIA: 1, MODOS: 11, ARMONIA2: 21 };
const PHYSICAL_PRODUCT_IDS = [1, 11, 21];

const BOOKS_DATA = {
  [PRODUCT_IDS.ARMONIA]: {
    titleKey: "illustrated_harmony_title",
    subtitlePages: 153,
    descriptionKey: "armonia_ilustrada.description",
    descriptionShortKey: "armonia_ilustrada.description_short",
    accentColor: "#d0ac22",
    rgbaColor: "rgba(208,172,34,0.6)",
    altName: "Armonía Ilustrada",
    trackName: "armonia",
    bookCode: "AI",
  },
  [PRODUCT_IDS.MODOS]: {
    titleKey: "illustrated_modes_title",
    subtitlePages: 190,
    descriptionKey: "modos_ilustrados.description",
    descriptionShortKey: "modos_ilustrados.description_short",
    accentColor: "#e588b5",
    rgbaColor: "rgba(229,136,181,0.6)",
    altName: "Modos Ilustrados",
    trackName: "modos",
    bookCode: "MI",
  },
  [PRODUCT_IDS.ARMONIA2]: {
    titleKey: "illustrated_harmony_2_title",
    subtitlePages: 218,
    descriptionKey: "armonia_ilustrada_2.description",
    descriptionShortKey: "armonia_ilustrada_2.description_short",
    accentColor: "#95ca44",
    rgbaColor: "rgba(149,202,68,0.6)",
    altName: "Armonía Ilustrada 2",
    trackName: "armonia2",
    bookCode: "AI2",
  },
};

const pickAllPhysicalRows = (list, cc, productId) => {
  const CC = String(cc || "").toUpperCase();
  const pid = Number(productId);
  const tries = [CC, ...(EUROPE_ISO_SET.has(CC) ? ["EU"] : []), "RW"];
  for (const code of tries) {
    const rows = list.filter(
      (r) =>
        Number(r?.productId) === pid &&
        !!r?.enabled &&
        String(r?.countryCode || "").toUpperCase() === code
    );
    if (rows.length > 0) return rows;
  }
  return [];
};

const roundToStep = (value, step) => Math.round(value / step) * step;

// ─── Book Section with gallery + physical buy options ───
function BookPhysicalSection({ bookData, langFromPath, userLang, physicalData, currency, fmtPrice, onBuyPhysicalClick, setLightboxImage, isWideScreen }) {
  const { t } = useTranslation("books");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const galleryRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  const images = bookGallery(bookData.bookCode, langFromPath);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const firstItem = galleryRefs.current[0];
      if (!firstItem) return;
      const itemWidth = firstItem.offsetWidth + 8;
      const scrollLeft = container.scrollLeft;
      const idx = Math.round(scrollLeft / itemWidth);
      setCurrentImageIdx(Math.min(Math.max(idx, 0), images.length - 1));
    };
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [images.length]);

  const fullDescription = getEs(bookData.descriptionKey);
  const truncatedDescription = getEs(bookData.descriptionShortKey);
  const productId = PHYSICAL_PRODUCT_IDS.find((pid) => BOOKS_DATA[pid] === bookData);

  return (
    <section
      style={{
        background: "#fff",
        padding: "10px 20px 50px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {!isWideScreen && (
        <>
          <div
            style={{
              position: "absolute", top: 0, left: -35, width: 80, height: 800,
              background: `radial-gradient(ellipse at 0% 50%, ${bookData.rgbaColor} 20%, ${bookData.rgbaColor.replace("0.6)", "0)")} 70%)`,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute", top: 0, right: -35, width: 80, height: 800,
              background: `radial-gradient(ellipse at 100% 50%, ${bookData.rgbaColor} 20%, ${bookData.rgbaColor.replace("0.6)", "0)")} 70%)`,
              pointerEvents: "none",
            }}
          />
        </>
      )}

      <div style={{ maxWidth: 500, margin: "0 auto", position: "relative", zIndex: 1 }}>
        {/* Header con barrita de color y título */}
        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 2 }}>
          <div style={{ display: "flex", height: 20, marginTop: 8, marginRight: 6 }}>
            <div style={{ width: 5, height: "100%", background: bookData.accentColor }} />
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 700, color: "#111", margin: 0, lineHeight: 1.3 }}>
            {getEs("book")} <span style={{ fontStyle: "italic" }}>{getEs(bookData.titleKey)}</span>
          </h2>
        </div>

        {/* Subtítulo - páginas */}
        <p style={{ color: "#999", fontSize: 14, marginBottom: 10, textAlign: "left" }}>
          {t("pages_other", { count: bookData.subtitlePages })}
        </p>

        {/* Galería de imágenes scrolleable */}
        <div
          ref={scrollContainerRef}
          style={{
            overflowX: "auto", overflowY: "hidden", scrollSnapType: "x mandatory",
            WebkitOverflowScrolling: "touch", scrollbarWidth: "none", msOverflowStyle: "none",
            marginBottom: 20,
          }}
        >
          <div className="d-flex" style={{ gap: 8, paddingRight: "8%" }}>
            {images.map((src, idx) => (
              <div
                key={idx}
                ref={(el) => (galleryRefs.current[idx] = el)}
                style={{ flexShrink: 0, width: "92%", scrollSnapAlign: "start", cursor: "pointer" }}
                onClick={() => {
                  if (idx === currentImageIdx) {
                    setLightboxImage(src);
                  } else {
                    const container = scrollContainerRef.current;
                    const targetEl = galleryRefs.current[idx];
                    if (container && targetEl) {
                      container.scrollTo({ left: targetEl.offsetLeft, behavior: "smooth" });
                    }
                  }
                }}
              >
                <Image
                  width={1000} height={707} quality={60} className="img-fluid"
                  style={{ aspectRatio: "1000/707", objectFit: "cover", borderRadius: 0, boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
                  src={src}
                  alt={idx === 0 ? `Tapa ${bookData.altName}` : `${bookData.altName} página ${idx}`}
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Descripción con truncado */}
        <div className="text-gray" style={{ fontSize: 16, lineHeight: 1.7, textAlign: "left", marginBottom: 20 }}>
          <p style={{ margin: 0 }}>
            {showFullDescription ? fullDescription : truncatedDescription}
          </p>
          {!showFullDescription && (
            <div style={{ textAlign: "right" }}>
              <button
                type="button"
                onClick={() => setShowFullDescription(true)}
                style={{ background: "none", border: "none", color: "#999", fontWeight: 600, cursor: "pointer", padding: 0, marginTop: 0, fontSize: 14 }}
              >
                {t("read_more")}
              </button>
            </div>
          )}
        </div>

        {/* Opciones libro físico */}
        {physicalData?.length > 0 && physicalData.map((physical, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 0,
              marginTop: 20,
            }}
          >
            {/* Botón */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
              <div
                style={{
                  // Sin stock el anillo va gris: el color de acento es una
                  // invitación a comprar y acá no hay nada para comprar.
                  background: physical.outOfStock ? "#dcdcdc" : bookData.accentColor,
                  padding: "2px",
                  borderRadius: "999px",
                }}
              >
                <a
                  href={physical.url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-mod btn-circle btn-large"
                  style={{
                    background: "#fff",
                    border: "none",
                    borderRadius: "999px",
                    color: physical.outOfStock ? "#999" : "#111",
                    paddingLeft: 10,
                    paddingRight: 10,
                    minWidth: 140,
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  data-btn-animate="y"
                  onClick={onBuyPhysicalClick(productId, physical.url)}
                >
                  {t("l2f_see_on")} {physical.source || "Tienda"}
                </a>
              </div>
              {userLang !== "es" && (
                <div style={{ fontSize: 13, color: "#999", fontWeight: 500, marginTop: 6, textAlign: "center", width: "100%" }}>
                  {t("physical_only_spanish")}
                </div>
              )}
            </div>

            {/* Precio */}
            <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              {/*
                Sin stock ocupa el lugar del precio en vez de sumarse abajo.
                El precio guardado es el último que se le conoció y hoy no se puede
                pagar: mostrarlo grande y en negro promete algo que no está.
              */}
              {physical.outOfStock ? (
                <div style={{ fontSize: 22, fontWeight: 700, color: "#999", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                  {t("physical_out_of_stock")}
                </div>
              ) : physical.price != null ? (
                <>
                  {physical.original != null && (
                    <div style={{ fontSize: 14, color: "#999", textDecoration: "line-through", lineHeight: 1.2 }}>
                      {physical.currency} {fmtPrice(physical.original)}
                    </div>
                  )}
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#111", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                    {physical.currency} {fmtPrice(physical.price)}*
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 26, fontWeight: 700, color: "#111", lineHeight: 1.3, whiteSpace: "nowrap" }}>
                  {/* Mientras carga el precio: un espacio que guarda el alto (antes iba una raya). */}
                  {"\u00A0"}
                </div>
              )}
              <div style={{ fontSize: 13, color: "#999", lineHeight: 1.4, whiteSpace: "normal" }}>
                {t("physical_book_apps_videos")}
                {/*
                  Sin stock no se muestra "el precio puede no estar actualizado": el
                  precio ya no se está mostrando, y aclarar sobre algo que no está a la
                  vista confunde en vez de ayudar.
                */}
                {!physical.outOfStock && physical.maybeOutdated && physical.source !== "Mercado Libre" && (
                  <><br />{t("physical_price_maybe_outdated")}</>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Main component ───
export default function HomeLibro2Fisicos() {
  const { t, i18n } = useTranslation("books");
  const searchParams = useSearchParamsDelVisitante();
  const pathname = usePathname();

  const SUPPORTED = TODOS;
  const langFromPath = useMemo(() => {
    const seg = (pathname?.split("/")?.[1] || "").toLowerCase();
    if (SUPPORTED.includes(seg)) return seg;
    const i18nLang = (i18n.language || "").toLowerCase().split("-")[0];
    return SUPPORTED.includes(i18nLang) ? i18nLang : "es";
  }, [pathname, i18n.language]);

  const localePath = (path) => `/${langFromPath}${path}`;

  const [isWideScreen, setIsWideScreen] = useState(false);
  useEffect(() => {
    const check = () => setIsWideScreen(window.innerWidth > 1000);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // === Country detection ===
  const [countryCode, setCountryCode] = usePaisDetectado();

  // === States ===
  const [physicalBooks, setPhysicalBooks] = useState([]);
  const [physicalByProduct, setPhysicalByProduct] = useState({});
  const [pricesMatrix, setPricesMatrix] = useState(null);
  const [pricingCountry, setPricingCountry] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [displayLocale, setDisplayLocale] = useState("en-US");
  const [lightboxImage, setLightboxImage] = useState(null);
  const hasTrackedViewListRef = useRef(false);

  // Track ViewItemList on mount
  useEffect(() => {
    if (!hasTrackedViewListRef.current && countryCode) {
      hasTrackedViewListRef.current = true;
      trackViewItemList(countryCode === "AR" ? "ARS" : "USD");
    }
  }, [countryCode]);

  // Override country via ?cc query param
  useEffect(() => {
    const q = normalizeCC(searchParams.get("cc"));
    if (q) {
      setCountryCode((prev) => {
        const current = String(prev || "").toUpperCase();
        if (q !== current) {
          Cookies.set("cc", q, { expires: 30, sameSite: "Lax" });
          return q;
        }
        return prev;
      });
    }
  }, [searchParams]);

  // Fetch physical books + prices matrix
  useEffect(() => {
    const ac = new AbortController();
    const fetchData = async () => {
      try {
        const [physicalRes, pricesRes] = await Promise.all([
          api.get("/api/physical-books", { params: { enabled: 1 }, signal: ac.signal }),
          api.get("/api/prices/matrix", { params: { product_ids: "1,11,21" }, signal: ac.signal }),
        ]);

        // Physical books: API may return { data: [...] } or [...]
        const rawPhysical = physicalRes?.data;
        const books = Array.isArray(rawPhysical?.data)
          ? rawPhysical.data
          : Array.isArray(rawPhysical)
          ? rawPhysical
          : [];
        setPhysicalBooks(books);

        setPricesMatrix(pricesRes?.data ?? null);
      } catch (e) {
        if (e.name !== "AbortError") console.error("[libro2-fisicos] Error fetching:", e);
      }
    };
    fetchData();
    return () => ac.abort();
  }, []);

  // Process prices matrix to get pricingCountry (same logic as home-libro)
  useEffect(() => {
    if (!countryCode || !pricesMatrix) return;

    try {
      const rawCountries = pricesMatrix?.countries ?? [];

      const countries = rawCountries.map((c) => ({
        ...c,
        code: String(c.code || "").toUpperCase(),
        currencyCode: String(c.currencyCode || "").toUpperCase(),
        originalRoundStep: c.originalRoundStep ?? c.original_round_step ?? null,
        displayLocale: c.displayLocale ?? c.display_locale ?? null,
      }));

      if (!countries.length) {
        setCurrency("USD");
        setDisplayLocale("en-US");
        return;
      }

      const byCode = {};
      for (const c of countries) {
        byCode[c.code] = c;
      }

      const cc = String(countryCode || "").toUpperCase();

      let base = byCode[cc] || null;
      if (!base && EUROPE_ISO_SET.has(cc)) {
        base = byCode["EU"] || null;
      }
      if (!base) {
        base = byCode["RW"] || null;
      }
      if (!base) {
        base = [...countries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))[0];
      }

      setPricingCountry(base);

      const computedLocale = base.displayLocale || (cc === "AR" ? "es-AR" : "en-US");
      setDisplayLocale(computedLocale);
      setCurrency(base.currencyCode || "USD");
    } catch (e) {
      console.error("[libro2-fisicos] Error processing prices:", e);
      setCurrency("USD");
    }
  }, [countryCode, pricesMatrix]);

  // Process physical books — fallback to US if no rows for country
  useEffect(() => {
    if (!physicalBooks?.length || !countryCode || !pricingCountry) return;

    try {
      const list = Array.isArray(physicalBooks) ? physicalBooks : [];
      const updated = {};

      const mapRows = (rows) =>
        rows.map((row) => {
          const price = row.price != null ? Number(row.price) : null;
          const disc = row.discount != null ? Number(row.discount) : null;
          const curr = String(row.currency || "").toUpperCase() || null;

          let roundedOriginal = null;
          if (price != null && Number.isFinite(disc) && disc > 0 && disc < 100) {
            const rawOriginal = price / (1 - disc / 100);
            const step = Number(pricingCountry?.originalRoundStep);
            roundedOriginal =
              Number.isFinite(step) && step > 0 ? roundToStep(rawOriginal, step) : rawOriginal;
          }

          return {
            enabled: true,
            url: row.url || null,
            price,
            original: roundedOriginal,
            currency: curr,
            source: row.source || null,
            maybeOutdated: !!row.maybeOutdated,
            outOfStock: !!row.outOfStock,
          };
        });

      for (const pid of PHYSICAL_PRODUCT_IDS) {
        const rows = pickAllPhysicalRows(list, countryCode, pid);
        if (rows.length > 0) {
          updated[pid] = mapRows(rows);
          continue;
        }
        // Fallback to US
        const fallbackRows = pickAllPhysicalRows(list, "US", pid);
        if (fallbackRows.length > 0) {
          updated[pid] = mapRows(fallbackRows);
        } else {
          updated[pid] = [];
        }
      }

      setPhysicalByProduct(updated);
    } catch (e) {
      console.error("[libro2-fisicos] Error procesando físicos:", e);
      setPhysicalByProduct({});
    }
  }, [physicalBooks, countryCode, pricingCountry]);

  // Price formatting
  const currencyExponent = (curr) => {
    const C = String(curr || "USD").toUpperCase();
    if (["JPY", "KRW", "VND", "CLP", "ISK", "UGX", "PYG", "RWF", "GNF", "XOF", "XAF"].includes(C)) return 0;
    if (["BHD", "JOD", "KWD", "OMR", "TND", "LYD"].includes(C)) return 3;
    return 2;
  };

  const fmtNumber = (value, curr = currency) =>
    typeof value === "number" && !Number.isNaN(value)
      ? new Intl.NumberFormat(displayLocale, {
          minimumFractionDigits: currencyExponent(curr),
          maximumFractionDigits: currencyExponent(curr),
        }).format(value)
      : "—";

  const fmtPrice = (v) => fmtNumber(Number(v), currency);

  const onBuyPhysicalClick = (productId, url) => (e) => {
    try {
      e.preventDefault();
      if (!url) return;
      window.open(url, "_blank", "noopener,noreferrer");
      const book = BOOKS_DATA[productId];
      try { trackLead(book?.trackName || String(productId), currency, undefined, url); } catch {}
    } catch (err) {
      console.error("Error in physical click handler", err);
    }
  };

  return (
    <>
      {/* Intro section */}
      <section style={{ background: "#fff", padding: "100px 20px 10px", textAlign: "center" }}>
        <div style={{ maxWidth: 500, margin: "0 auto" }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: "#111", marginBottom: 12 }}>
            {t("l2f_title")}
          </h1>
          <p style={{ color: "#666", fontSize: 15, lineHeight: 1.6, marginBottom: 0 }}>
            {t("l2f_subtitle")}
          </p>
        </div>
      </section>

      {/* Book sections — same style as pack/full */}
      {PHYSICAL_PRODUCT_IDS.map((pid) => (
        <BookPhysicalSection
          key={pid}
          bookData={BOOKS_DATA[pid]}
          langFromPath="es"
          userLang={langFromPath}
          physicalData={physicalByProduct[pid]}
          currency={currency}
          fmtPrice={fmtPrice}
          onBuyPhysicalClick={onBuyPhysicalClick}
          setLightboxImage={setLightboxImage}
          isWideScreen={isWideScreen}
        />
      ))}

      {/* Back link */}
      <section style={{ background: "#fff", padding: "0 20px 60px", textAlign: "center" }}>
        <a
          href={localePath("/libros")}
          style={{ color: "#999", fontSize: 14, textDecoration: "underline", textUnderlineOffset: 3 }}
        >
          {t("l2f_back")}
        </a>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.9)", zIndex: 9999,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => setLightboxImage(null)}
        >
          <Image
            src={lightboxImage}
            alt={t("enlarged_view")}
            width={1200}
            height={848}
            quality={90}
            style={{ maxWidth: "95vw", maxHeight: "95vh", objectFit: "contain" }}
          />
        </div>
      )}
    </>
  );
}
